<script>
import confirmationModal from "../components/modals/modal-confirmation.vue";

export default {
  name: "McpPage",
  components: { confirmationModal },
  data: () => ({
    loading: true,
    config: null,
    connections: [],
    error: "",
    client: "claude",
    disconnecting: null,
    toDisconnect: { id: null, clientName: "" },
    clients: [
      { key: "claude", label: "Claude" },
      { key: "codex", label: "Codex" },
      { key: "grok", label: "Grok" },
    ],
    capabilities: [
      { title: "Read proofs", tag: "", text: "Find proofs, inspect feedback and summarize version changes within your existing access." },
      { title: "Read review progress", tag: "Optional", text: "Include stages, approvers and recorded decisions." },
      { title: "Create proofs and versions", tag: "Off by default", text: "Create draft proofs and new versions, and attach files by download link. Nothing is sent to approvers." },
      { title: "Send new versions", tag: "Off by default", text: "Send a new version to its current stage. Emails the stage's approvers and reviewers." },
      { title: "Comment", tag: "Off by default", text: "Add notes, reply, resolve or reopen feedback. Existing email notifications may apply." },
      { title: "Diagnose webhooks", tag: "Off by default", text: "Check one webhook's recent statistics, events and delivery attempts. Requires account-management permission." },
      { title: "Manage webhooks", tag: "Off by default", text: "Set up receivers, select events, pause delivery and replay a failed event. Can send account-wide data to external services; includes diagnostics." },
    ],
    prompts: [
      "Summarize the open feedback on my packaging proof.",
      "What changed between versions 2 and 3?",
      "Which proofs are waiting for a decision?",
      "Create a proof called Spring Catalog v1 with the PDF at this link.",
      "Why did this webhook fail to deliver? Check its latest attempt.",
    ],
  }),
  computed: {
    serverUrl() { return this.config && this.config.serverUrl; },
    claudeCommand() { return `claude mcp add --transport http --scope user ashore ${this.serverUrl}\nclaude mcp login ashore`; },
    codexCommand() { return `codex mcp add ashore --url ${this.serverUrl}\ncodex mcp login ashore`; },
    grokCommand() { return `grok mcp add --transport http ashore ${this.serverUrl}`; },
    grokPrompt() { return `Add Ashore as a remote MCP server using ${this.serverUrl}. Use OAuth and let me complete sign-in and choose permissions. Start with read-only access.`; },
    guideLink() {
      return ({
        claude: { href: "https://code.claude.com/docs/en/mcp", label: "Claude Code MCP Guide" },
        codex: { href: "https://developers.openai.com/codex/mcp", label: "Codex MCP Guide" },
        grok: { href: "https://docs.x.ai/build/features/mcp-servers", label: "Grok Build MCP Guide" },
      })[this.client];
    },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true; this.error = "";
      try {
        const [config, connections] = await Promise.all([window.$A.Api.get("/mcp/configuration"), window.$A.Api.get("/mcp/connections")]);
        this.config = config.data;
        this.connections = connections.data.map(c => ({ id: c.id || c.Id, clientName: c.clientName || c.ClientName, scope: c.scope || c.Scope }));
      } catch { this.error = "Could not load your MCP connections. Please try again."; }
      finally { this.loading = false; }
    },
    async copy(value, label) {
      try { await navigator.clipboard.writeText(value); this.$A.AlertUser(`${label} copied`, "success"); }
      catch { this.$A.AlertUser("Copy is unavailable in this browser. Select and copy the text instead.", "warning"); }
    },
    confirmDisconnect(connection) {
      this.toDisconnect = connection;
      this.$bvModal.show("disconnectMcpConnection");
    },
    async disconnect() {
      const connection = this.toDisconnect;
      if (!connection.id) return;
      this.disconnecting = connection.id;
      try {
        await window.$A.Api.delete("/mcp/connections/" + encodeURIComponent(connection.id));
        this.connections = this.connections.filter(c => c.id !== connection.id);
        this.$A.AlertUser("Assistant disconnected", "success");
      } catch { this.$A.AlertUser("Could not disconnect. Please try again.", "warning"); }
      finally { this.disconnecting = null; }
    },
    scopeLabel(scope) { return ({ identity: "Identity", "proofs:read": "Read proofs", "proofs:write": "Create proofs and versions", "proofs:send": "Send versions", "workflow:read": "Review progress", "comments:write": "Comments enabled", "webhooks:read": "Webhook diagnostics", "webhooks:write": "Manage webhooks" })[scope] || scope; },
    navigateToBilling() { this.$router.push("/billing"); },
  },
};
</script>

<template>
  <div class="nav-margin mcp-page">
    <navigation />

    <div class="page-body">
      <main class="mcp-main">
        <div class="mcp-main-inner">
          <div class="page-head">
            <h1 class="page-title">MCP Connections</h1>
            <p class="page-subtitle">Bring proof status, feedback, version changes and integration diagnostics into Claude, Codex or Grok.</p>
          </div>

          <div class="section-stack">
            <div v-if="loading" class="settings-card">
              <div class="list-empty" role="status">
                <div class="empty-text">Loading your connections…</div>
              </div>
            </div>

            <div v-if="error" class="settings-card">
              <div class="list-empty" role="alert">
                <div class="empty-title">Something went wrong</div>
                <div class="empty-text">{{ error }}</div>
                <a class="secondary-btn empty-cta" @click="load">Try again</a>
              </div>
            </div>

            <template v-if="config">
              <!-- MCP not enabled in this environment -->
              <div v-if="!config.enabled" class="settings-card">
                <div class="list-empty">
                  <div class="empty-title">MCP is coming soon to this environment</div>
                  <div class="empty-text">Connection setup will appear here when your Ashore server is ready.</div>
                </div>
              </div>

              <!-- Plan gate -->
              <div v-else-if="!config.planAllowed" class="settings-card gate-card">
                <div class="gate-body">
                  <span class="gate-badge">Premium</span>
                  <h2 class="gate-title">Connect your <span class="gate-highlight">AI assistant</span> to Ashore</h2>
                  <p class="gate-description">
                    Let Claude, Codex or Grok read proof status, summarize feedback and diagnose
                    integrations with access you choose.
                  </p>
                  <ul class="gate-features">
                    <li v-for="item in capabilities" :key="item.title">
                      <img src="/img/check-circle.svg" alt="" class="gate-check" />
                      {{ item.title }}
                    </li>
                  </ul>
                  <p class="gate-description gate-availability">Available on Premium and Enterprise plans.</p>
                </div>
                <div class="gate-foot">
                  <button type="button" class="primary-btn" @click="navigateToBilling">Upgrade Plan</button>
                </div>
              </div>

              <template v-else>
                <!-- Connect -->
                <div class="settings-card">
                  <div class="card-head">
                    <div class="card-head-title">Connect an assistant</div>
                    <div class="card-head-subtitle">Use this server address in your AI app. Sign in to Ashore, then choose its access.</div>
                  </div>
                  <div class="card-body">
                    <div class="field">
                      <label class="field-label">Server address</label>
                      <div class="code-row">
                        <code class="code-value">{{ serverUrl }}</code>
                        <a class="secondary-btn" @click="copy(serverUrl, 'Server address')">Copy</a>
                      </div>
                    </div>

                    <div class="field">
                      <label class="field-label">Your AI app</label>
                      <div class="segmented" role="tablist" aria-label="AI assistant">
                        <button
                          v-for="c in clients"
                          :key="c.key"
                          type="button"
                          role="tab"
                          :id="'tab-' + c.key"
                          aria-controls="mcp-instructions"
                          :aria-selected="client === c.key"
                          class="segment"
                          :class="{ 'is-active': client === c.key }"
                          @click="client = c.key"
                        >{{ c.label }}</button>
                      </div>
                    </div>

                    <div role="tabpanel" id="mcp-instructions" :aria-labelledby="'tab-' + client" class="instructions">
                      <template v-if="client === 'claude'">
                        <p class="field-help">Run these commands in your terminal for Claude Code, then sign in to Ashore and choose permissions in your browser.</p>
                        <div class="code-row code-row-block">
                          <pre class="code-block">{{ claudeCommand }}</pre>
                          <a class="action-link" @click="copy(claudeCommand, 'Claude commands')">Copy commands</a>
                        </div>
                        <p class="field-help">On Claude Code versions before 2.1.186, open <code>/mcp</code> inside Claude Code and authenticate Ashore instead of running the login command.</p>
                        <details class="more">
                          <summary>Using Claude in the browser or desktop app?</summary>
                          <p class="field-help">Open <strong>Customize → Connectors → + → Add custom connector</strong>, name it Ashore and paste the server address. Complete Ashore sign-in and choose permissions.</p>
                          <a class="action-link" href="https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp" target="_blank" rel="noopener noreferrer">Claude connector guide</a>
                        </details>
                      </template>

                      <template v-else-if="client === 'codex'">
                        <p class="field-help">Run these commands in the Codex CLI, then finish sign-in in your browser.</p>
                        <div class="code-row code-row-block">
                          <pre class="code-block">{{ codexCommand }}</pre>
                          <a class="action-link" @click="copy(codexCommand, 'Codex commands')">Copy commands</a>
                        </div>
                      </template>

                      <template v-else>
                        <p class="field-help">Run this command in your terminal for Grok Build. On first use, complete Ashore sign-in and choose permissions in your browser.</p>
                        <div class="code-row code-row-block">
                          <pre class="code-block">{{ grokCommand }}</pre>
                          <a class="action-link" @click="copy(grokCommand, 'Grok command')">Copy command</a>
                        </div>
                        <p class="field-help">You can also open <code>/mcps</code> in Grok Build, select Ashore and press <code>i</code> to authenticate.</p>
                        <details class="more">
                          <summary>Using Grok Bot?</summary>
                          <p class="field-help">Grok Bot connects separately from the CLI. Use this setup request where custom servers are supported, complete the presented OAuth flow yourself, then attach Ashore with <strong>@</strong> in chat.</p>
                          <div class="code-row code-row-block">
                            <pre class="code-block">{{ grokPrompt }}</pre>
                            <a class="action-link" @click="copy(grokPrompt, 'Grok Bot setup request')">Copy setup request</a>
                          </div>
                          <p class="field-help">Custom server setup depends on your Grok Bot version and your team's connector policy. This is a custom connection, not an Ashore Marketplace listing.</p>
                          <a class="action-link" href="https://docs.x.ai/grok-bot/computer-and-apps#connect-an-app" target="_blank" rel="noopener noreferrer">Grok Bot connection guide</a>
                        </details>
                      </template>
                    </div>

                    <p class="field-help muted-note">Your AI app may require a paid plan or administrator approval for custom connectors.</p>
                  </div>
                  <div class="card-foot">
                    <a class="secondary-btn" :href="guideLink.href" target="_blank" rel="noopener noreferrer">{{ guideLink.label }}</a>
                  </div>
                </div>

                <!-- Capabilities -->
                <div class="settings-card">
                  <div class="card-head">
                    <div class="card-head-title">Choose what it can do</div>
                    <div class="card-head-subtitle">You pick the access level when you sign in. To change it later, disconnect and connect again.</div>
                  </div>
                  <div class="list-rows">
                    <div class="list-row" v-for="item in capabilities" :key="item.title">
                      <div class="row-main">
                        <div class="row-title">
                          {{ item.title }}
                          <span v-if="item.tag" class="row-tag">{{ item.tag }}</span>
                        </div>
                        <div class="row-text">{{ item.text }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="card-foot card-foot-note">
                    Approving proofs, deleting content, sending workflows, managing users and billing are not available through this connection.
                  </div>
                </div>

                <!-- Try asking -->
                <div class="settings-card">
                  <div class="card-head">
                    <div class="card-head-title">Try asking</div>
                    <div class="card-head-subtitle">Your assistant reads only proofs you can access. Always review its interpretation before acting on it.</div>
                  </div>
                  <div class="card-body">
                    <blockquote v-for="prompt in prompts" :key="prompt" class="prompt">“{{ prompt }}”</blockquote>
                  </div>
                </div>

                <!-- Connections -->
                <div class="settings-card">
                  <div class="card-head">
                    <div class="card-head-title">Your connections</div>
                    <div class="card-head-subtitle">Connections belong to your user account. To change permissions, disconnect and connect again.</div>
                  </div>

                  <div v-if="!connections.length" class="list-empty">
                    <div class="empty-title">No assistants connected yet</div>
                    <div class="empty-text">Follow the steps above to connect Claude, Codex or Grok. Connected assistants will appear here.</div>
                  </div>

                  <div v-else class="list-rows">
                    <div class="list-row list-row-head">
                      <span class="row-main">Assistant</span>
                      <span class="row-actions"></span>
                    </div>
                    <div class="list-row" v-for="connection in connections" :key="connection.id">
                      <div class="row-main">
                        <div class="row-title">{{ connection.clientName }}</div>
                        <div class="chips">
                          <span v-for="scope in connection.scope.split(' ')" :key="scope" class="chip">{{ scopeLabel(scope) }}</span>
                        </div>
                      </div>
                      <span class="row-actions">
                        <a
                          class="action-link action-danger"
                          :class="{ 'is-busy': disconnecting === connection.id }"
                          @click="confirmDisconnect(connection)"
                        >{{ disconnecting === connection.id ? "Disconnecting…" : "Disconnect" }}</a>
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </div>
        </div>
      </main>
    </div>

    <confirmationModal
      v-on:confirmed="disconnect"
      name="disconnectMcpConnection"
      confirm-button-text="Disconnect"
      question="Disconnect this assistant?"
      :body="'It will lose access to Ashore immediately. <br/><br/> <b>Disconnecting:</b> ' + toDisconnect.clientName"
    />
  </div>
</template>

<style scoped>
.page-body {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px); /* clear position:fixed nav */
}

/* Main column, same width and padding as account/profile settings */
.mcp-main {
  flex: 1;
  min-width: 0;
  background: #f4f5f7;
}
.mcp-main-inner {
  max-width: 991px;
  margin: 0 auto;
  padding: 24px;
}
.section-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Page head */
.page-head {
  margin-bottom: 20px;
}
.page-title {
  margin: 0;
  font-size: 22px;
  line-height: 28px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #111827;
}
.page-subtitle {
  margin: 4px 0 0;
  font-size: 13.5px;
  color: #6b7280;
  max-width: 640px;
}

/* Card */
.settings-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
}
.card-head {
  padding: 14px 20px;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px 12px 0 0;
}
.card-head-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}
.card-head-subtitle {
  margin-top: 1px;
  font-size: 12.5px;
  font-weight: 400;
  color: #6b7280;
}
.card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
}
.card-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  background: #fbfbfc;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 0 0 12px 12px;
}
.card-foot-note {
  justify-content: flex-start;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

/* Fields */
.field-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.field-help {
  margin: 0;
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.55;
}
.field-help code,
.instructions code {
  font-size: 12px;
  padding: 1px 5px;
  border-radius: 4px;
  background: #f3f4f6;
  color: #111827;
}
.muted-note {
  font-size: 12px;
}

/* Code rows */
.code-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 8px 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
}
.code-value {
  font-size: 13px;
  color: #111827;
  overflow-wrap: anywhere;
}
.code-row-block {
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background: #fbfbfc;
}
.code-block {
  margin: 0;
  width: 100%;
  font-size: 12.5px;
  line-height: 1.6;
  color: #111827;
  background: transparent;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* Segmented control */
.segmented {
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  background: #f3f4f6;
  border-radius: 8px;
}
.segment {
  height: 30px;
  padding: 0 14px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 120ms ease, color 120ms ease;
}
.segment:hover {
  color: #111827;
}
.segment.is-active {
  background: #fff;
  color: #017a48;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.08);
}

.instructions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.more {
  padding: 12px 14px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 8px;
  background: #fbfbfc;
}
.more summary {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
}
.more[open] summary {
  margin-bottom: 10px;
}
.more .field-help + .action-link,
.more .code-row {
  margin-top: 10px;
}
.more .action-link {
  display: inline-block;
}

/* Rows */
.list-rows {
  display: flex;
  flex-direction: column;
}
.list-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.list-row:last-child {
  border-bottom: 0;
}
.list-row-head {
  padding: 8px 20px;
  background: #fbfbfc;
}
.list-row-head span {
  font-size: 10.5px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.row-main {
  flex: 1;
  min-width: 0;
}
.row-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.row-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
}
.row-text {
  margin-top: 2px;
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.5;
}
.row-actions {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}
.chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(1, 146, 86, 0.08);
  color: #017a48;
  font-size: 11px;
  font-weight: 600;
}

/* Prompts */
.prompt {
  margin: 0;
  padding: 10px 14px;
  border-left: 3px solid #019256;
  border-radius: 0 6px 6px 0;
  background: #fbfbfc;
  font-size: 13px;
  color: #374151;
}

/* Empty state */
.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 32px;
}
.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
.empty-text {
  margin-top: 5px;
  font-size: 12.5px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.55;
  max-width: 420px;
}
.empty-cta {
  margin-top: 16px;
}

/* Plan gate */
.gate-card {
  overflow: hidden;
}
.gate-body {
  padding: 32px;
}
.gate-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: 999px;
  background: rgba(1, 146, 86, 0.1);
  color: #017a48;
  font-size: 12px;
  font-weight: 600;
}
.gate-title {
  margin: 14px 0 0;
  font-size: 26px;
  line-height: 32px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #111827;
}
.gate-highlight {
  color: #019256;
}
.gate-description {
  margin: 10px 0 0;
  font-size: 14px;
  color: #4b5563;
}
.gate-availability {
  margin-top: 16px;
  font-size: 13px;
  color: #6b7280;
}
.gate-features {
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}
.gate-features li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 5px 0;
  font-size: 13.5px;
  color: #4b5563;
}
.gate-check {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}
.gate-foot {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px;
  background: #fbfbfc;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

/* Buttons and links */
.primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 18px;
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  text-decoration: none;
  transition: background-color 120ms ease;
}
.primary-btn:hover {
  background: #017a48;
  color: #fff;
  text-decoration: none;
}
.secondary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 14px;
  background: #fff !important;
  color: #1f2937 !important;
  border: 1px solid rgba(15, 23, 42, 0.12) !important;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
}
.secondary-btn:hover,
.secondary-btn:focus {
  border-color: #019256 !important;
  color: #019256 !important;
  text-decoration: none;
}
.action-link {
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  color: #019256;
  text-decoration: none;
}
.action-link:hover {
  color: #017a48;
  text-decoration: none;
}
.action-danger {
  color: #b43643;
}
.action-danger:hover {
  color: #8f2a35;
}
.action-link.is-busy {
  pointer-events: none;
  opacity: 0.6;
}

/* Mobile */
@media (max-width: 991px) {
  .mcp-main-inner {
    padding: 16px;
  }
}
@media (max-width: 767px) {
  .code-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .gate-body {
    padding: 24px 20px;
  }
}
</style>
